import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Menu, X } from 'lucide-react';

gsap.registerPlugin(ScrollTrigger);

const Navbar = () => {
    const navRef = useRef(null);
    const [isScrolled, setIsScrolled] = useState(false);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const location = useLocation();

    // Close mobile menu on route change
    useEffect(() => {
        setIsMobileMenuOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        // Watch for scroll position
        const handleScroll = () => {
            if (window.scrollY > 50) {
                setIsScrolled(true);
            } else {
                setIsScrolled(false);
            }
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Prevent scrolling when mobile menu is open
    useEffect(() => {
        if (isMobileMenuOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }
        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isMobileMenuOpen]);

    return (
        <>
            <nav
                ref={navRef}
                className={`fixed top-5 lg:top-7 left-1/2 -translate-x-1/2 z-[60] transition-all duration-500 rounded-full px-5 lg:px-8 py-4 flex items-center justify-between gap-6 lg:gap-10 w-[95%] max-w-6xl
                ${isScrolled || isMobileMenuOpen
                        ? 'bg-[#F0EFF4]/90 backdrop-blur-xl border border-[#0A0A14]/10 shadow-lg text-[#18181B]'
                        : 'bg-[#0A0A14]/40 backdrop-blur-md border border-[#F0EFF4]/10 text-[#F0EFF4]'
                    }`}
            >
                {/* Logo */}
                <Link to="/" className="font-bold tracking-tight text-xl lg:text-2xl link-hover whitespace-nowrap">
                    RoofClaw
                </Link>

                {/* Desktop Menu — only shown at ≥1024px to avoid squish on tablets/landscape phones */}
                <div className="hidden lg:flex items-center gap-7 font-medium text-sm">
                    <Link to="/product" className="link-hover opacity-80 hover:opacity-100 transition-opacity whitespace-nowrap">The System</Link>
                    <Link to="/use-cases" className="link-hover opacity-80 hover:opacity-100 transition-opacity whitespace-nowrap">Use Cases</Link>
                    <Link to="/hardware" className="link-hover opacity-80 hover:opacity-100 transition-opacity whitespace-nowrap">Hardware</Link>
                    <Link to="/why" className="link-hover opacity-80 hover:opacity-100 transition-opacity whitespace-nowrap">Why Buy</Link>
                    <Link to="/process" className="link-hover opacity-80 hover:opacity-100 transition-opacity whitespace-nowrap">Our Process</Link>
                    <Link to="/blog" className="link-hover opacity-80 hover:opacity-100 transition-opacity whitespace-nowrap">Blog</Link>
                </div>

                {/* Desktop CTA & Mobile Toggle */}
                <div className="flex items-center gap-4">
                    <Link to="/portal" className="hidden lg:inline-flex border border-[#7B61FF]/50 text-[#7B61FF] hover:bg-[#7B61FF]/10 px-4 py-2 rounded-full text-sm font-semibold tracking-wide whitespace-nowrap transition-colors">
                        Client Login
                    </Link>
                    <Link to="/pricing" className="hidden lg:inline-flex btn-magnetic bg-[#7B61FF] text-[#F0EFF4] px-5 py-2 rounded-full text-sm font-semibold tracking-wide shadow-[0_0_15px_rgba(123,97,255,0.4)] whitespace-nowrap">
                        See Pricing
                    </Link>

                    {/* Hamburger Button — shown below 1024px (portrait + landscape phones, tablets) */}
                    <button
                        className="lg:hidden p-2 rounded-full hover:bg-black/5 transition-colors"
                        onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                        aria-label="Toggle Menu"
                    >
                        {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
                    </button>
                </div>
            </nav>

            {/* Mobile Menu Overlay */}
            <div
                className={`fixed inset-0 bg-[#0A0A14] z-[55] transition-transform duration-500 ease-[cubic-bezier(0.25,0.46,0.45,0.94)] flex flex-col justify-center items-center ${isMobileMenuOpen ? 'translate-y-0' : '-translate-y-full'}`}
            >
                <div className="flex flex-col items-center gap-8 text-2xl font-bold text-[#F0EFF4]">
                    <Link to="/" className="hover:text-[#7B61FF] transition-colors" onClick={() => setIsMobileMenuOpen(false)}>Home</Link>
                    <Link to="/product" className="hover:text-[#7B61FF] transition-colors" onClick={() => setIsMobileMenuOpen(false)}>The System</Link>
                    <Link to="/use-cases" className="hover:text-[#7B61FF] transition-colors" onClick={() => setIsMobileMenuOpen(false)}>Use Cases</Link>
                    <Link to="/hardware" className="hover:text-[#7B61FF] transition-colors" onClick={() => setIsMobileMenuOpen(false)}>Hardware</Link>
                    <Link to="/blog" className="hover:text-[#7B61FF] transition-colors" onClick={() => setIsMobileMenuOpen(false)}>Intelligence (Blog)</Link>
                    <Link to="/why" className="hover:text-[#7B61FF] transition-colors" onClick={() => setIsMobileMenuOpen(false)}>Why Buy</Link>
                    <Link to="/process" className="hover:text-[#7B61FF] transition-colors" onClick={() => setIsMobileMenuOpen(false)}>Our Process</Link>

                    <Link to="/portal" className="mt-8 border-2 border-[#7B61FF]/50 text-[#7B61FF] px-10 py-4 rounded-full text-lg" onClick={() => setIsMobileMenuOpen(false)}>
                        Client Login
                    </Link>
                    <Link to="/#reserve" className="bg-[#7B61FF] text-[#F0EFF4] px-10 py-4 rounded-full text-lg shadow-[0_0_30px_rgba(123,97,255,0.4)]" onClick={() => setIsMobileMenuOpen(false)}>
                        Reserve Build
                    </Link>
                </div>
            </div>
        </>
    );
};

export default Navbar;
